﻿import {useMemo} from "react";
import {useMediaQuery, useTheme} from "@mui/material";

const DEFAULT_DESKTOP_ZOOM = 8.7;
const DEFAULT_TABLET_ZOOM = 8.6;
const DEFAULT_PHONE_ZOOM = 8.2;

// Web Mercator: metres per pixel at zoom 0 on the equator (earth's circumference / 256 px).
// https://developers.google.com/maps/documentation/javascript/coordinates
const EQUATOR_METRES_PER_PX = 40075016.686 / 256;
const METRES_PER_MILE = 1609.344;
const MILES_PER_DEGREE_LAT = 69.09;

const mercatorY = (lat: number) => Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360));

/**
 * The zoom that puts a map's nearer edge `radiusMiles` from its center, so the whole
 * radius is on screen at any map size (securspace-ui#3088). The height is sized on the
 * Mercator projection itself, since the north half of a map covers less ground than the
 * south; rounded down, so every edge is at or just past the radius.
 */
export const zoomToFitRadius = (radiusMiles: number, lat: number, widthPx: number, heightPx: number) => {
  const sideways = Math.log2((EQUATOR_METRES_PER_PX * Math.cos((lat * Math.PI) / 180) * (widthPx / 2)) / (radiusMiles * METRES_PER_MILE));
  // A zoom-0 world is 256 px for 2π of Mercator y.
  const northward = Math.log2(((heightPx / 2) * 2 * Math.PI) / (256 * (mercatorY(lat + radiusMiles / MILES_PER_DEGREE_LAT) - mercatorY(lat))));
  return Math.floor(Math.min(sideways, northward) * 100) / 100;
};

export const useDefaultMapZoom = () => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  return useMemo(() => {
    if (isPhone) {
      return DEFAULT_PHONE_ZOOM;
    }
    if (isTablet) {
      return DEFAULT_TABLET_ZOOM;
    }
    return DEFAULT_DESKTOP_ZOOM;
  }, [isPhone, isTablet]);
}
